import {
	createContext,
	type FC,
	isValidElement,
	type PropsWithChildren,
	type ReactNode,
	type RefAttributes,
	useContext,
} from "react";
import {
	ToggleButton as AriaToggleButton,
	ToggleButtonGroup as AriaToggleButtonGroup,
	type ToggleButtonGroupProps,
	type ToggleButtonProps,
} from "react-aria-components";

import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

import { styles } from "./button-group-styles";

type ButtonSize = keyof typeof styles.sizes;

const ButtonGroupContext = createContext<{ size: ButtonSize }>({ size: "md" });

interface ButtonGroupItemProps
	extends RefAttributes<HTMLButtonElement>, ToggleButtonProps {
	className?: string;
	iconLeading?: FC<{ className?: string }> | ReactNode;
	iconTrailing?: FC<{ className?: string }> | ReactNode;
	onClick?: () => void;
}

export const ButtonGroupItem = ({
	children,
	className,
	iconLeading: IconLeading,
	iconTrailing: IconTrailing,
	...otherProps
}: PropsWithChildren<ButtonGroupItemProps>) => {
	const context = useContext(ButtonGroupContext);

	if (!context) {
		throw new Error(
			"ButtonGroupItem must be used within a ButtonGroup component",
		);
	}

	const { size } = context;

	const isIcon = (IconLeading || IconTrailing) && !children;

	return (
		<AriaToggleButton
			{...otherProps}
			className={cx(styles.common.root, styles.sizes[size].root, className)}
			data-icon-leading={IconLeading ? true : undefined}
			data-icon-only={isIcon ? true : undefined}
		>
			{isReactComponent(IconLeading) && (
				<IconLeading
					className={cx(styles.common.icon, styles.sizes[size].icon)}
				/>
			)}
			{isValidElement(IconLeading) && IconLeading}

			{children}

			{isReactComponent(IconTrailing) && (
				<IconTrailing
					className={cx(styles.common.icon, styles.sizes[size].icon)}
				/>
			)}
			{isValidElement(IconTrailing) && IconTrailing}
		</AriaToggleButton>
	);
};

interface ButtonGroupProps
	extends
		Omit<ToggleButtonGroupProps, "orientation">,
		RefAttributes<HTMLDivElement> {
	className?: string;
	size?: ButtonSize;
}

export const ButtonGroup = ({
	children,
	className,
	size = "md",
	...otherProps
}: ButtonGroupProps) => {
	return (
		<ButtonGroupContext.Provider value={{ size }}>
			<AriaToggleButtonGroup
				className={cx(
					"relative z-0 inline-flex w-max -space-x-px rounded-lg shadow-xs",
					className,
				)}
				selectionMode="single"
				{...otherProps}
			>
				{children}
			</AriaToggleButtonGroup>
		</ButtonGroupContext.Provider>
	);
};
